import {useCallback, useMemo, useState} from 'react';
import {useDocumentNotifications} from './DocumentNotificationsProvider';

export type RefusalFinishedAlertKind = 'accepted' | 'declined' | 'mixed';

export type NotificationRowHighlightKind = 'accepted' | 'declined';

export type NotificationRowHighlights = Record<string, NotificationRowHighlightKind>;

type RefusalFinishedAlertData = {
    kind: RefusalFinishedAlertKind;
    title: string;
    visible: boolean;
    orderItemPks: string[];
    rowHighlights: NotificationRowHighlights;
    close: () => void;
    acknowledge: () => Promise<void>;
};

const toPositiveNumber = (value: unknown): number => {
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? value : 0;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    return 0;
};

export const useRefusalFinishedAlert = (): RefusalFinishedAlertData | null => {
    const {refusalFinishedNotifications, markRefusalFinishedAsRead, refresh} =
        useDocumentNotifications();

    const [dismissedKey, setDismissedKey] = useState<string | null>(null);

    // Не показываем alert для refusal_finished с payload = null
    const visibleNotifications = useMemo(() => {
        return refusalFinishedNotifications.filter((item) => item.payload != null);
    }, [refusalFinishedNotifications]);

    const alertKey = useMemo(() => {
        return visibleNotifications
            .map((item) => item.id)
            .sort()
            .join('|');
    }, [visibleNotifications]);

    const orderItemPks = useMemo(() => {
        const unique = new Set<string>();

        visibleNotifications.forEach((item) => {
            const value = item.payload?.orderItemPk;

            if (value !== null && value !== undefined) {
                const normalized = String(value).trim();

                if (normalized) {
                    unique.add(normalized);
                }
            }
        });

        return Array.from(unique);
    }, [visibleNotifications]);

    const rowHighlights = useMemo<NotificationRowHighlights>(() => {
        const highlights: NotificationRowHighlights = {};

        visibleNotifications.forEach((item) => {
            const value = item.payload?.orderItemPk;

            if (value === null || value === undefined) {
                return;
            }

            const orderItemPk = String(value).trim();

            if (!orderItemPk) {
                return;
            }

            const accepted = toPositiveNumber(item.payload?.accepted) > 0;
            const declined = toPositiveNumber(item.payload?.declined) > 0;

            if (!accepted && !declined) {
                return;
            }

            // declined важнее, если вдруг для одной строки пришли оба признака
            if (declined) {
                highlights[orderItemPk] = 'declined';
                return;
            }

            if (accepted && highlights[orderItemPk] !== 'declined') {
                highlights[orderItemPk] = 'accepted';
            }
        });

        return highlights;
    }, [visibleNotifications]);

    const summary = useMemo(() => {
        if (!visibleNotifications.length) {
            return null;
        }

        const orderMap = new Map<string, {accepted: boolean; declined: boolean}>();

        visibleNotifications.forEach((item) => {
            const accepted = toPositiveNumber(item.payload?.accepted) > 0;
            const declined = toPositiveNumber(item.payload?.declined) > 0;

            if (!accepted && !declined) {
                return;
            }

            const orderKey = String(item.payload?.orderItemPk ?? item.id);
            const prev = orderMap.get(orderKey) ?? {accepted: false, declined: false};

            orderMap.set(orderKey, {
                accepted: prev.accepted || accepted,
                declined: prev.declined || declined
            });
        });

        const orders = Array.from(orderMap.values());
        const totalOrders = orders.length;
        const declinedOrders = orders.filter((item) => item.declined).length;

        if (!totalOrders) {
            return null;
        }

        const hasAccepted = orders.some((item) => item.accepted);
        const hasDeclined = orders.some((item) => item.declined);

        if (hasAccepted && hasDeclined) {
            return {
                kind: 'mixed' as const,
                title:
                    declinedOrders > 1
                        ? `Несколько ваших заявок на отмену отклонены (${declinedOrders})`
                        : 'Несколько ваших заявок на отмену отклонены'
            };
        }

        if (hasAccepted) {
            return {
                kind: 'accepted' as const,
                title:
                    totalOrders > 1
                        ? `Ваши заявки на отмену одобрены (${totalOrders})`
                        : 'Ваша заявка на отмену одобрена'
            };
        }

        return {
            kind: 'declined' as const,
            title:
                totalOrders > 1
                    ? `Ваши заявки на отмену отклонены (${totalOrders})`
                    : 'Ваша заявка на отмену отклонена'
        };
    }, [visibleNotifications]);

    const close = useCallback(() => {
        setDismissedKey(alertKey);
    }, [alertKey]);

    const acknowledge = useCallback(async () => {
        setDismissedKey(alertKey);

        await markRefusalFinishedAsRead();
        await refresh();
    }, [alertKey, markRefusalFinishedAsRead, refresh]);

    if (!summary) {
        return null;
    }

    return {
        kind: summary.kind,
        title: summary.title,
        visible: dismissedKey !== alertKey,
        orderItemPks,
        rowHighlights,
        close,
        acknowledge
    };
};
